import { stickerUrl } from "@listou/shared/stickers";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { viewerQuery } from "../../lib/session";
import { TOOLS } from "../../lib/tools";
import { Sticker } from "../list-page/parts";
import { SiteFooter, SiteHeader } from "../site-header";

export type ToolCopy = {
  h1: string;
  intro: string;
  sections: { heading: string; paragraphs: string[] }[];
  faq: { question: string; answer: string }[];
};

/** The free tools: a pastel hero, the tool on a raised card, then the copy and the FAQ. */
export function ToolLayout({
  copy,
  eyebrow,
  stickers = [stickerUrl("bebe", "cigogne"), stickerUrl("bebe", "lune")],
  tabs,
  children,
  after,
  disclaimer,
}: {
  copy: ToolCopy;
  eyebrow: string;
  stickers?: [string, string];
  tabs?: { label: string; to: string; current: boolean }[];
  /** The tool itself, on the raised card. */
  children: ReactNode;
  /** Anything between the card and the copy: a chart, a gallery. */
  after?: ReactNode;
  disclaimer?: string;
}) {
  const viewer = useQuery({ ...viewerQuery, retry: false });
  return (
    <div className="min-h-dvh overflow-x-clip">
      <div className="bg-[linear-gradient(160deg,#fde8ee_0%,#fdf3e1_55%,#e9f2fa_100%)]">
        <SiteHeader signedIn={viewer.isSuccess} />
        <header className="relative mx-auto grid max-w-4xl justify-items-center gap-5 px-4 pt-8 pb-28 text-center sm:px-6 sm:pt-12">
          <Sticker
            src={stickers[0]}
            className="absolute top-2 -left-2 w-20 -rotate-12 drop-shadow-[0_6px_10px_rgba(31,37,68,0.18)] sm:left-4 sm:w-28"
          />
          <Sticker
            src={stickers[1]}
            className="absolute right-0 bottom-20 w-16 rotate-12 drop-shadow-[0_6px_10px_rgba(31,37,68,0.18)] sm:right-6 sm:w-24"
          />
          <p className="rounded-full bg-surface/80 px-4 py-1 text-sm font-bold text-accent-text">
            {eyebrow}
          </p>
          <h1 className="max-w-[20ch] text-4xl font-medium sm:text-6xl">{copy.h1}</h1>
          <p className="max-w-[56ch] text-lg text-ink/80 sm:text-xl">{copy.intro}</p>
          {tabs && (
            <nav aria-label="Méthodes" className="flex flex-wrap justify-center gap-2 pt-2">
              {tabs.map((tab) => (
                <Link
                  key={tab.to}
                  to={tab.to}
                  className={tab.current ? "btn btn-sm" : "btn-secondary btn-sm"}
                >
                  {tab.label}
                </Link>
              ))}
            </nav>
          )}
        </header>
      </div>

      <main className="relative mx-auto -mt-20 grid max-w-4xl gap-14 px-4 pb-20 sm:px-6">
        <section
          aria-label="Calculateur"
          className="grid gap-6 rounded-[2rem] border-[1.5px] border-line bg-surface p-5 shadow-[0_24px_50px_-30px_rgba(31,37,68,0.45)] sm:p-8"
        >
          {children}
        </section>

        {after}

        <div className="grid gap-10 sm:grid-cols-2">
          {copy.sections.map((section) => (
            <section key={section.heading} className="grid content-start gap-3">
              <h2 className="text-2xl font-semibold sm:text-3xl">{section.heading}</h2>
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph} className="leading-relaxed text-ink/85">
                  {paragraph}
                </p>
              ))}
            </section>
          ))}
        </div>

        <aside className="relative grid justify-items-start gap-3 overflow-hidden rounded-[2rem] bg-[#eef4fa] p-6 sm:p-10">
          <Sticker
            src={stickerUrl("bebe", "poussette")}
            className="absolute -right-4 -bottom-4 w-32 rotate-6 opacity-90 sm:w-44"
          />
          <h2 className="text-3xl font-semibold">Et la liste de naissance ?</h2>
          <p className="max-w-[48ch] text-lg text-ink/80">
            Beaucoup de parents la commencent vers le 5e mois : le temps de choisir sans se presser,
            et pour les proches de s'organiser. Sur Listou, chacun réserve son cadeau, sans doublon
            et sans gâcher la surprise.
          </p>
          <Link to="/" className="btn">
            Créer ma liste de naissance
          </Link>
        </aside>

        {copy.faq.length > 0 && (
          <section className="grid gap-3">
            <h2 className="text-3xl font-semibold">Questions fréquentes</h2>
            {copy.faq.map((item) => (
              <details key={item.question} className="panel group">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-5 text-lg font-bold [&::-webkit-details-marker]:hidden">
                  {item.question}
                  <span
                    aria-hidden
                    className="grid size-8 shrink-0 place-items-center rounded-full bg-ink/[0.06] text-xl leading-none transition group-open:rotate-45"
                  >
                    +
                  </span>
                </summary>
                <p className="px-5 pb-5 leading-relaxed text-ink/85">{item.answer}</p>
              </details>
            ))}
          </section>
        )}

        <ToolLinks />

        {disclaimer && <p className="text-sm text-muted">{disclaimer}</p>}
      </main>
      <SiteFooter />
    </div>
  );
}

function ToolLinks() {
  return (
    <nav aria-label="Autres outils" className="grid gap-3">
      <h2 className="text-2xl font-semibold">Les autres outils</h2>
      <div className="flex flex-wrap gap-2">
        {TOOLS.map((tool) => (
          <Link key={tool.to} to={tool.to} className="btn-secondary btn-sm">
            {tool.label}
          </Link>
        ))}
      </div>
    </nav>
  );
}

export const faqJsonLd = (faq: ToolCopy["faq"]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faq.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: { "@type": "Answer", text: item.answer },
  })),
});
